import React, { useState } from 'react';

// A meal pictured as a small plated stack: the first sticker is the main item (large, low); the others rest on its
// top edge, smaller and slightly tilted. A container main item (bowl, glass, plate…) holds its toppings: they're drawn
// behind its front wall. A sticker's badge (an almond on a glass of milk) sits on its bottom corner. Each sticker's `box` ([x, y, w, h] of the image) is where the food actually sits, so the
// overlap holds whatever the shapes. Decorative: the dish name next to it says what it is.

const HEX = 'polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)';

function place(box, contentW, maxH, centerX, contentTop) {
  const [bx, by, bw, bh] = box || [0, 0, 1, 1];
  const w = Math.min(contentW, (maxH * bw) / bh);
  const imgW = w / bw;
  return { left: centerX - w / 2 - bx * imgW, top: contentTop - by * imgW, width: imgW, contentH: (w * bh) / bw };
}

export function plate(stickers) {
  if (stickers.length === 1) {
    const p = place(stickers[0].box, 72, 72, 50, 0);
    return [{ ...p, top: p.top + (100 - p.contentH) / 2, rotate: 0 }];
  }
  const tuckIn = !!stickers[0].vessel;
  const baseTop = 94 - place(stickers[0].box, 76, 56, 50, 0).contentH;
  const out = [{ ...place(stickers[0].box, 76, 56, 50, baseTop), rotate: 0 }];
  const tops = stickers.slice(1);
  const centers = tops.length === 1 ? [64] : [32, 68];
  const rotations = tops.length === 1 ? [8] : [-10, 9];
  const width = tops.length === 1 ? 46 : 40;
  tops.forEach((s, i) => {
    const probe = place(s.box, width, 42, centers[i], 0);
    const contentTop = Math.max(2, baseTop - probe.contentH * (tuckIn ? 0.72 : 0.65));
    out.push({ ...place(s.box, width, 42, centers[i], contentTop), rotate: rotations[i] });
  });
  return out;
}

export default function MealStickerTile({ stickers, size = 40, className = '' }) {
  const [broken, setBroken] = useState({});
  const list = (stickers || []).filter((s) => s?.url && !broken[s.slug]).slice(0, 3);
  if (!list.length) return null;
  const spots = plate(list);
  const shadow = size >= 56 ? '0 2px 3px rgba(0,0,0,.22)' : '0 1px 1.5px rgba(0,0,0,.25)';
  const imgs = list.map((s, i) => (
    <img
      key={`${s.slug}-${i}`}
      src={s.url}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => setBroken((b) => ({ ...b, [s.slug]: true }))}
      className="absolute max-w-none select-none"
      style={{
        left: `${spots[i].left}%`,
        top: `${spots[i].top}%`,
        width: `${spots[i].width}%`,
        transform: spots[i].rotate ? `rotate(${spots[i].rotate}deg)` : undefined,
        filter: `drop-shadow(${shadow})`,
      }}
    />
  ));
  // Badges go on top of everything, at the bottom corner (end side) of their food's real outline.
  const badges = list.map((s, i) => {
    if (!s.badge?.url) return null;
    const [bx, by, bw, bh] = s.box || [0, 0, 1, 1];
    const sp = spots[i];
    const d = Math.max(sp.width * bw * 0.42, 22); // % of the tile
    const left = sp.left + sp.width * (bx + bw) - d * 0.8;
    const top = sp.top + sp.width * (by + bh) - d * 0.9;
    return (
      <span
        key={`badge-${i}`}
        className="absolute flex items-center justify-center rounded-full bg-surface ring-1 ring-line dark:bg-surface-dark dark:ring-line-dark"
        style={{ left: `${left}%`, top: `${top}%`, width: `${d}%`, height: `${d}%` }}
      >
        <img src={s.badge.url} alt="" loading="lazy" decoding="async" draggable={false} className="h-[78%] w-[78%] object-contain" />
      </span>
    );
  });
  const tuckIn = list.length > 1 && list[0].vessel;
  return (
    <span aria-hidden="true" className={`relative inline-block shrink-0 ${className}`} style={{ width: size, height: size }}>
      <span className="absolute bg-forest-faint dark:bg-forest-faint-dark" style={{ inset: '4% 10%', clipPath: HEX }} />
      {tuckIn ? [...imgs.slice(1), imgs[0]] : imgs}
      {badges}
    </span>
  );
}
